import React, { useState } from "react";
import Draggable from "react-draggable";
import {
  Row,
  Col,
  List,
  Empty,
  Tooltip,
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Switch,
  Button,
  Row as AntRow,
  Col as AntCol,
  message,
  Popover,
  Divider,
  Space,
  Tag,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  TagOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import "./DayView.css";
import { Event, CalendarCategory } from "./activityCalendar";
import api from "../../../services/api";

const { TextArea } = Input;
const { Option } = Select;

interface DayViewProps {
  currentDate: Dayjs;
  events: Event[];
  categories: CalendarCategory[];
  onEventClick: (event: Event) => void;
  onEventsChange: (events: Event[]) => void;
  onEventSaved?: () => void;
  selectedCompany: { id: string; type: string } | null;
}

const DayView: React.FC<DayViewProps> = ({
  currentDate,
  events,
  categories,
  onEventClick,
  onEventsChange,
  onEventSaved,
}) => {
  const signature = localStorage.getItem("signature");
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isEventDetailVisible, setIsEventDetailVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [formPosition, setFormPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [detailPosition, setDetailPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [activePopover, setActivePopover] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();

  // Create time slots in 12-hour format like WeekView
  const timeSlots: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    const period = hour < 12 ? "AM" : "PM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    timeSlots.push(`${displayHour}:00 ${period}`);
  }

  const getEventsForHour = (hour: number): Event[] => {
    return events.filter((event) => {
      if (event.allDay) return false;
      return (
        event.start.isSame(currentDate, "day") && event.start.hour() === hour
      );
    });
  };

  // Get latest event for an hour (sorted by start time)
  const getLatestEventForHour = (hour: number) => {
    const hourEvents = getEventsForHour(hour);
    return hourEvents
      .sort((a, b) => b.start.valueOf() - a.start.valueOf())
      .slice(0, 1)[0];
  };

  // Get remaining events count for an hour
  const getRemainingEventsCount = (hour: number) => {
    const hourEvents = getEventsForHour(hour);
    return Math.max(0, hourEvents.length - 1);
  };

  // Get all events for an hour (for popover)
  const getAllEventsForHour = (hour: number) => {
    return getEventsForHour(hour);
  };

  const getAllDayEvents = (): Event[] => {
    return events.filter(
      (event) => event.allDay && event.start.isSame(currentDate, "day")
    );
  };

  // Get latest all-day event
  const getLatestAllDayEvent = () => {
    const allDayEvents = getAllDayEvents();
    return allDayEvents
      .sort((a, b) => b.start.valueOf() - a.start.valueOf())
      .slice(0, 1)[0];
  };

  // Get remaining all-day events count
  const getRemainingAllDayEventsCount = () => {
    const allDayEvents = getAllDayEvents();
    return Math.max(0, allDayEvents.length - 1);
  };

  const formatEventTime = (event: Event): string => {
    if (event.allDay) return "All day";
    return `${event.start.format("h:mm A")} - ${event.end.format("h:mm A")}`;
  };

  // Handle event click - show detail card instead of modal
  const handleEventClick = (event: Event, clickEvent: React.MouseEvent) => {
    setSelectedEvent(event);
    setActivePopover(null); // Close any open popover

    const container = document.querySelector(".day-view") as HTMLElement;
    const containerRect = container.getBoundingClientRect();

    let x = clickEvent.clientX - containerRect.left;
    let y = clickEvent.clientY - containerRect.top;

    const cardWidth = 400;
    const cardHeight = 500;

    if (x + cardWidth > containerRect.width) {
      x = containerRect.width - cardWidth - 10;
    }
    if (y + cardHeight > containerRect.height) {
      y = containerRect.height - cardHeight - 10;
    }
    if (x < 0) x = 10;
    if (y < 0) y = 10;

    setDetailPosition({ x, y });
    setIsEventDetailVisible(true);
  };

  // Handle all day switch change
  const handleAllDayChange = (allDay: boolean) => {
    const start = form.getFieldValue("start");
    const end = form.getFieldValue("end");

    if (allDay) {
      if (start) {
        form.setFieldValue("start", start.startOf("day"));
      }
      if (end) {
        form.setFieldValue("end", end.endOf("day"));
      }
    } else {
      const now = dayjs();
      if (start) {
        form.setFieldValue(
          "start",
          start.startOf("day").set("hour", 9).set("minute", 0)
        );
      }
      if (end) {
        form.setFieldValue(
          "end",
          end.startOf("day").set("hour", 17).set("minute", 0)
        );
      }
    }
  };

  const handleTimeSlotClick = (
    hour: number,
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    const timeSlot = timeSlots[hour];
    const [hourStr] = timeSlot.split(":");
    const hourNum = parseInt(hourStr);
    const isPM = timeSlot.includes("PM");
    let actualHour = hourNum;
    if (isPM && hourNum !== 12) actualHour += 12;
    if (!isPM && hourNum === 12) actualHour = 0;

    const rect = event.currentTarget.getBoundingClientRect();
    const clickY = event.clientY - rect.top;
    const cellHeight = rect.height;
    const isAboveCenter = clickY < cellHeight / 2;

    const startHour = actualHour;
    const endHour = (actualHour + 1) % 24;
    const startMinute = isAboveCenter ? 0 : 30;
    const endMinute = isAboveCenter ? 0 : 30;
    const startTime = currentDate
      .set("hour", startHour)
      .set("minute", startMinute);

    const endTime = currentDate.set("hour", endHour).set("minute", endMinute);

    const container = document.querySelector(".day-view") as HTMLElement;
    const containerRect = container.getBoundingClientRect();

    let x = event.clientX - containerRect.left;
    let y = event.clientY - containerRect.top;

    const cardWidth = 400;
    const cardHeight = 400;
    if (x + cardWidth > containerRect.width) {
      x = containerRect.width - cardWidth - 10;
    }
    if (y + cardHeight > containerRect.height) {
      y = containerRect.height - cardHeight - 10;
    }
    if (x < 0) x = 10;
    if (y < 0) y = 10;
    setFormPosition({ x, y });
    setIsFormVisible(true);
    setIsEditing(false);
    setActivePopover(null); // Close any open popover

    form.resetFields();
    form.setFieldsValue({
      title: "",
      description: "",
      start: startTime,
      end: endTime,
      allDay: false,
      category: categories[0]?.name || "Meeting",
      color: categories[0]?.color || "#1890ff",
      is_public: false,
    });
  };

  const handleSaveEvent = () => {
    form.validateFields().then(async (values) => {
      let { start, end, allDay } = values;

      if (allDay) {
        start = start.startOf("day");
        end = end.endOf("day");
      }

      const categoryId =
        categories.find((c) => c.name === values.category)?.id || "1";

      if (isEditing && selectedEvent) {
        // Update existing event
        const data = {
          signature,
          id: selectedEvent.id,
          title: values.title,
          description: values.description,
          start_date: start.format("YYYY-MM-DD HH:mm:ss"),
          end_date: end.format("YYYY-MM-DD HH:mm:ss"),
          is_all_day: allDay,
          event_type: parseInt(categoryId),
          is_public: values.is_public || false,
        };
        try {
          await api.post("activityCalendarService/updateEvent", data);
          onEventSaved?.();
          setIsFormVisible(false);
          setFormPosition(null);
          setIsEditing(false);
          setSelectedEvent(null);
          message.success("Event updated successfully");
        } catch (err) {
          message.error("Failed to update event");
        }
      } else {
        // Create new event
        const data = {
          signature,
          title: values.title,
          description: values.description,
          start_date: start.format("YYYY-MM-DD HH:mm:ss"),
          end_date: end.format("YYYY-MM-DD HH:mm:ss"),
          is_all_day: allDay,
          event_type: parseInt(categoryId),
          is_public: values.is_public || false,
        };
        try {
          await api.post("activityCalendarService/createEvent", data);
          onEventSaved?.();
          setIsFormVisible(false);
          setFormPosition(null);
          message.success("Event saved successfully");
        } catch (err) {
          message.error("Failed to create event");
        }
      }
    });
  };

  const handleCancelEvent = () => {
    setIsFormVisible(false);
    setFormPosition(null);
    setIsEditing(false);
    setSelectedEvent(null);
  };

  // Handle edit event - open the form card with event data
  const handleEditEvent = () => {
    if (selectedEvent) {
      // Close detail card and open form card in the same position
      setIsEventDetailVisible(false);
      setIsFormVisible(true);
      setIsEditing(true);

      // Use the same position as the detail card
      if (detailPosition) {
        setFormPosition(detailPosition);
      }

      // Pre-fill the form with event data
      form.setFieldsValue({
        title: selectedEvent.title,
        description: selectedEvent.description,
        start: selectedEvent.start,
        end: selectedEvent.end,
        allDay: selectedEvent.allDay,
        category: selectedEvent.category,
        color: selectedEvent.color,
        is_public: selectedEvent.is_public || false,
      });
    }
  };

  // Handle delete event
  const handleDeleteEvent = async () => {
    if (!selectedEvent?.id) return;

    try {
      await api.post("activityCalendarService/deleteEvent", {
        signature,
        id: selectedEvent.id,
      });
      message.success("Event deleted successfully");
      setIsEventDetailVisible(false);
      setSelectedEvent(null);
      onEventSaved?.();
    } catch (err) {
      message.error("Failed to delete event");
    }
  };

  const handleCloseDetail = () => {
    setIsEventDetailVisible(false);
    setSelectedEvent(null);
  };

  // Handle popover visibility change
  const handlePopoverVisibleChange = (visible: boolean, popoverId: string) => {
    if (visible) {
      setActivePopover(popoverId);
    } else {
      setActivePopover(null);
    }
  };

  // Popover content for remaining events in an hour
  const renderRemainingEventsPopover = (hour: number) => {
    const hourEvents = getAllEventsForHour(hour);

    return (
      <div className="remaining-events-popover">
        <div className="popover-title">
          All Events at {timeSlots[hour]} on {currentDate.format("MMM D")}
        </div>
        <div className="popover-events-list">
          {hourEvents.map((event) => (
            <div
              key={event.id}
              className="popover-event-item"
              onClick={(e) => {
                e.stopPropagation();
                handleEventClick(event, e as any);
              }}
            >
              <div
                className="popover-event-color"
                style={{ backgroundColor: event.color }}
              />
              <div className="popover-event-content">
                <div className="popover-event-title">{event.title}</div>
                <div className="popover-event-time">
                  {formatEventTime(event)}
                </div>
                {event.description && (
                  <div className="popover-event-description">
                    {event.description}
                  </div>
                )}
                {event.is_public && (
                  <div className="popover-event-public">🌐 Public Event</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Popover content for remaining all-day events
  const renderAllDayRemainingEventsPopover = () => {
    const allDayEvents = getAllDayEvents();

    return (
      <div className="remaining-events-popover">
        <div className="popover-title">
          All Day Events on {currentDate.format("MMM D")}
        </div>
        <div className="popover-events-list">
          {allDayEvents.map((event) => (
            <div
              key={event.id}
              className="popover-event-item"
              onClick={(e) => {
                e.stopPropagation();
                handleEventClick(event, e as any);
              }}
            >
              <div
                className="popover-event-color"
                style={{ backgroundColor: event.color }}
              />
              <div className="popover-event-content">
                <div className="popover-event-title">{event.title}</div>
                {event.description && (
                  <div className="popover-event-description">
                    {event.description}
                  </div>
                )}
                {event.is_public && (
                  <div className="popover-event-public">🌐 Public Event</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const allDayEvents = getAllDayEvents();
  const latestAllDayEvent = getLatestAllDayEvent();
  const remainingAllDayCount = getRemainingAllDayEventsCount();
  const isAllDay = Form.useWatch("allDay", form);

  return (
    <div className="day-view" style={{ position: "relative" }}>
      <div className="day-header">
        <div className="day-title">
          {currentDate.format("dddd, MMMM ")}
          <span
            className={`day-number ${
              currentDate.isSame(dayjs(), "day") ? "today" : ""
            }`}
          >
            {currentDate.format("D")}
          </span>
          {currentDate.format(", YYYY")}
        </div>
        {allDayEvents.length > 0 && (
          <div className="all-day-section">
            <div className="all-day-label">All Day</div>
            <div className="all-day-events">
              {/* Show latest all-day event */}
              {latestAllDayEvent && (
                <Tooltip
                  key={latestAllDayEvent.id}
                  title={
                    latestAllDayEvent.description || latestAllDayEvent.title
                  }
                >
                  <div
                    className="all-day-event"
                    style={{
                      borderLeft: `4px solid ${latestAllDayEvent.color}`,
                    }}
                    onClick={(e) => handleEventClick(latestAllDayEvent, e)}
                  >
                    <span className="event-title">
                      {latestAllDayEvent.title}
                    </span>
                    {latestAllDayEvent.location && (
                      <span className="event-location">
                        {" "}
                        • {latestAllDayEvent.location}
                      </span>
                    )}
                  </div>
                </Tooltip>
              )}

              {/* Show remaining all-day events count with popover */}
              {remainingAllDayCount > 0 && (
                <Popover
                  content={renderAllDayRemainingEventsPopover()}
                  trigger="click"
                  placement="bottom"
                  overlayClassName="remaining-events-popover-overlay"
                  open={activePopover === "all-day-popover"}
                  onOpenChange={(visible) =>
                    handlePopoverVisibleChange(visible, "all-day-popover")
                  }
                >
                  <div
                    className="more-events-indicator"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="more-events-badge">
                      +{remainingAllDayCount} more
                    </div>
                  </div>
                </Popover>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="time-slots">
        {timeSlots.map((timeSlot, index) => {
          const latestEvent = getLatestEventForHour(index);
          const remainingCount = getRemainingEventsCount(index);
          const popoverId = `hour-${index}-popover`;

          return (
            <Row key={index} className="time-slot-row">
              <Col span={2} className="time-label">
                {timeSlot}
              </Col>
              <Col
                span={22}
                className="time-slot-content"
                onClick={(e) => handleTimeSlotClick(index, e)}
              >
                {latestEvent ? (
                  <div className="event-slot-container">
                    {/* Show latest event */}
                    <Tooltip
                      title={
                        <div>
                          <div>
                            <strong>{latestEvent.title}</strong>
                          </div>
                          <div>{formatEventTime(latestEvent)}</div>
                          {latestEvent.location && (
                            <div>📍 {latestEvent.location}</div>
                          )}
                          {latestEvent.description && (
                            <div>📝 {latestEvent.description}</div>
                          )}
                        </div>
                      }
                    >
                      <div
                        className="event-block"
                        style={{ borderLeft: `4px solid ${latestEvent.color}` }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEventClick(latestEvent, e);
                        }}
                      >
                        <div className="event-time">
                          {formatEventTime(latestEvent)}
                        </div>
                        <div className="event-title">{latestEvent.title}</div>
                        {latestEvent.location && (
                          <div className="event-location">
                            📍 {latestEvent.location}
                          </div>
                        )}
                      </div>
                    </Tooltip>

                    {/* Show remaining events count with popover */}
                    {remainingCount > 0 && (
                      <Popover
                        content={renderRemainingEventsPopover(index)}
                        trigger="click"
                        placement="bottom"
                        overlayClassName="remaining-events-popover-overlay"
                        open={activePopover === popoverId}
                        onOpenChange={(visible) =>
                          handlePopoverVisibleChange(visible, popoverId)
                        }
                      >
                        <div
                          className="more-events-indicator"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="more-events-badge">
                            +{remainingCount} more
                          </div>
                        </div>
                      </Popover>
                    )}
                  </div>
                ) : (
                  <div className="empty-slot">
                    <div className="time-guide">{timeSlot}</div>
                  </div>
                )}
              </Col>
            </Row>
          );
        })}
      </div>

      {/* Event Form Card */}
      {isFormVisible && formPosition && (
        <Draggable
          handle=".card-header"
          bounds=".day-view"
          defaultPosition={formPosition}
        >
          <Card
            className="event-form-card day-form-card"
            style={{
              width: 440,
              zIndex: 1000,
              position: "absolute",
              top: 0,
              left: 0,
            }}
            styles={{
              body: { padding: 0 },
            }}
          >
            {/* Custom Header */}
            <div className="card-header day-header">
              <div className="card-header-content">
                <div className="card-title-section">
                  <div className="card-icon">✨</div>
                  <div>
                    <div className="card-title">
                      {isEditing ? "Edit Event" : "New Event"}
                    </div>
                    <div className="card-subtitle">
                      {currentDate.format("MMM D, YYYY")}
                    </div>
                  </div>
                </div>
                <Button
                  type="text"
                  onClick={handleCancelEvent}
                  className="close-btn"
                  icon={<span>×</span>}
                />
              </div>
            </div>

            {/* Form Content */}
            <div className="card-body">
              <Form form={form} layout="vertical" className="event-form">
                {/* Quick Input Section */}
                <div className="quick-input-section">
                  <Form.Item
                    name="title"
                    rules={[
                      { required: true, message: "Please enter event title" },
                    ]}
                  >
                    <Input
                      placeholder="Event title..."
                      className="quick-input"
                      size="large"
                      variant="borderless"
                      autoFocus
                    />
                  </Form.Item>
                </div>

                {/* Compact Settings */}
                <div className="compact-settings">
                  <div className="setting-item">
                    <div className="setting-label">
                      <span className="setting-icon">🕒</span>
                      All Day
                    </div>
                    <Form.Item
                      name="allDay"
                      valuePropName="checked"
                      className="setting-control"
                    >
                      <Switch
                        onChange={handleAllDayChange}
                        className="compact-switch"
                        size="small"
                      />
                    </Form.Item>
                  </div>

                  <div className="setting-item">
                    <div className="setting-label">
                      <span className="setting-icon">🎯</span>
                      Category
                    </div>
                    <Form.Item name="category" className="setting-control">
                      <Select
                        className="compact-select"
                        placeholder="Category"
                        size="small"
                      >
                        {categories
                          .filter(
                            (cat) =>
                              cat.name !== "Birthday" &&
                              cat.name !== "Anniversary" &&
                              cat.name !== "Holiday"
                          )
                          .map((cat) => (
                            <Option key={cat.id} value={cat.name}>
                              <div className="category-option">
                                <div
                                  className="category-dot"
                                  style={{ backgroundColor: cat.color }}
                                />
                                <span>{cat.name}</span>
                              </div>
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </div>
                  <div className="setting-item">
                    <div className="setting-label">
                      <span className="setting-icon">🌐</span>
                      Public
                    </div>
                    <Form.Item
                      name="is_public"
                      valuePropName="checked"
                      className="setting-control"
                    >
                      <Switch className="compact-switch" size="small" />
                    </Form.Item>
                  </div>
                </div>

                {/* Time Selection */}
                <div className="time-selection">
                  <div className="time-inputs">
                    <Form.Item
                      name="start"
                      className="time-item"
                      rules={[
                        { required: true, message: "Start time required" },
                      ]}
                    >
                      <DatePicker
                        showTime={
                          !isAllDay
                            ? {
                                format: "h:mm A",
                                use12Hours: true,
                                hourStep: 1,
                                minuteStep: 5,
                                showNow: false,
                              }
                            : false
                        }
                        format={isAllDay ? "MMM D" : "h:mm A"}
                        className="time-picker"
                        disabled={isAllDay}
                        placeholder="Start"
                        size="small"
                      />
                    </Form.Item>

                    <div className="time-separator">→</div>

                    <Form.Item
                      name="end"
                      className="time-item"
                      rules={[{ required: true, message: "End time required" }]}
                    >
                      <DatePicker
                        showTime={
                          !isAllDay
                            ? {
                                format: "h:mm A",
                                use12Hours: true,
                                hourStep: 1,
                                minuteStep: 5,
                                showNow: false,
                              }
                            : false
                        }
                        format={isAllDay ? "MMM D" : "h:mm A"}
                        className="time-picker"
                        disabled={isAllDay}
                        placeholder="End"
                        size="small"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* Additional Details */}
                <div className="additional-details">
                  <Form.Item name="description">
                    <TextArea
                      rows={2}
                      placeholder="📝 Add description..."
                      className="detail-textarea"
                      variant="borderless"
                      size="small"
                    />
                  </Form.Item>
                </div>

                {/* Action Buttons */}
                <div className="compact-actions">
                  <Button
                    onClick={handleCancelEvent}
                    className="ghost-btn"
                    size="small"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSaveEvent}
                    className="primary-btn"
                    size="small"
                    icon={<span>✓</span>}
                  >
                    {isEditing ? "Update Event" : "Save Event"}
                  </Button>
                </div>
              </Form>
            </div>
          </Card>
        </Draggable>
      )}

      {/* Event Detail Card */}
      {isEventDetailVisible && selectedEvent && detailPosition && (
        <Draggable
          handle=".card-header"
          bounds=".day-view"
          defaultPosition={detailPosition}
        >
          <Card
            className="event-detail-card"
            style={{
              width: 400,
              zIndex: 1001,
              position: "absolute",
              top: 0,
              left: 0,
            }}
            styles={{
              body: { padding: 0 },
            }}
          >
            {/* Custom Header */}
            <div
              className="card-header detail-header"
              style={{
                background: `linear-gradient(135deg, ${selectedEvent.color}20 0%, ${selectedEvent.color}40 100%)`,
                borderLeft: `4px solid ${selectedEvent.color}`,
              }}
            >
              <div className="card-header-content">
                <div className="card-title-section">
                  <div
                    className="card-icon"
                    style={{ backgroundColor: selectedEvent.color }}
                  >
                    <CalendarOutlined
                      style={{ color: "white", fontSize: "16px" }}
                    />
                  </div>
                  <div>
                    <div className="card-title">{selectedEvent.title}</div>
                    <div className="card-subtitle">
                      {formatEventTime(selectedEvent)}
                    </div>
                  </div>
                </div>
                <Button
                  type="text"
                  onClick={handleCloseDetail}
                  className="close-btn"
                  icon={<CloseOutlined />}
                />
              </div>
            </div>

            {/* Detail Content */}
            <div className="card-body">
              <div className="event-details">
                {/* Event Information */}
                <div className="detail-section">
                  <div className="detail-item">
                    <div className="detail-label">
                      <ClockCircleOutlined className="detail-icon" />
                      Time
                    </div>
                    <div className="detail-value">
                      {selectedEvent.allDay ? (
                        "All Day"
                      ) : (
                        <>
                          {selectedEvent.start.format(
                            "ddd, MMM D, YYYY h:mm A"
                          )}
                          <br />
                          to{" "}
                          {selectedEvent.end.format("ddd, MMM D, YYYY h:mm A")}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-label">
                      <TagOutlined className="detail-icon" />
                      Category
                    </div>
                    <div className="detail-value">
                      <Tag
                        color={selectedEvent.color}
                        style={{ border: `1px solid ${selectedEvent.color}` }}
                      >
                        {selectedEvent.category}
                      </Tag>
                    </div>
                  </div>

                  {selectedEvent.location && (
                    <div className="detail-item">
                      <div className="detail-label">
                        <EnvironmentOutlined className="detail-icon" />
                        Location
                      </div>
                      <div className="detail-value">
                        {selectedEvent.location}
                      </div>
                    </div>
                  )}

                  <div className="detail-item">
                    <div className="detail-label">
                      <FileTextOutlined className="detail-icon" />
                      Visibility
                    </div>
                    <div className="detail-value">
                      <Tag color={selectedEvent.is_public ? "blue" : "default"}>
                        {selectedEvent.is_public
                          ? "Public Event"
                          : "Private Event"}
                      </Tag>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {selectedEvent.description && (
                  <>
                    <Divider style={{ margin: "16px 0" }} />
                    <div className="detail-section">
                      <div className="detail-item">
                        <div className="detail-label">
                          <FileTextOutlined className="detail-icon" />
                          Description
                        </div>
                        <div className="detail-value description-text">
                          {selectedEvent.description}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Action Buttons */}
                <Divider style={{ margin: "16px 0" }} />
                <div className="detail-actions">
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <Button
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteEvent}
                      size="middle"
                      disabled={selectedEvent?.is_generated}
                    >
                      Delete
                    </Button>
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={handleEditEvent}
                      size="middle"
                      disabled={selectedEvent?.is_generated}
                    >
                      Edit Event
                    </Button>
                  </Space>
                </div>
              </div>
            </div>
          </Card>
        </Draggable>
      )}
    </div>
  );
};

export default DayView;
